import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Calendar } from "lucide-react";

export default function AddLinkDialog({ open, onClose, onCreated, categories }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ogImage, setOgImage] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [publishNow, setPublishNow] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl(""); setTitle(""); setDescription(""); setOgImage("");
      setCategoryId(""); setExpiresAt(""); setPublishNow(true);
    }
  }, [open]);

  if (!open) return null;

  const fetchPreview = async () => {
    if (!url.trim()) { toast.error("Paste a URL first"); return; }
    setFetching(true);
    try {
      const r = await api.post("/links/preview", { url: url.trim() });
      if (r.data.title) setTitle(r.data.title);
      if (r.data.description) setDescription(r.data.description);
      if (r.data.og_image) setOgImage(r.data.og_image);
      toast.success("Preview loaded");
    } catch { toast.error("Couldn't fetch preview — enter details manually"); }
    finally { setFetching(false); }
  };

  const save = async () => {
    if (!url.trim()) { toast.error("URL is required"); return; }
    if (!/^https?:\/\//i.test(url.trim())) { toast.error("URL must start with http:// or https://"); return; }
    setSaving(true);
    try {
      const payload = {
        url: url.trim(),
        title: title.trim(),
        description: description.trim(),
        og_image: ogImage.trim(),
        category_id: categoryId || null,
        is_public: publishNow,
        expires_at: expiresAt ? new Date(expiresAt + "T23:59:59Z").toISOString() : null,
      };
      await api.post("/links", payload);
      toast.success(publishNow ? "Published to your public page" : "Saved as draft");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="add-link-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Publish</div>
            <div className="font-heading text-2xl tracking-tight mt-1">Add a link</div>
          </div>
          <button data-testid="add-link-close" onClick={onClose} className="text-zinc-400 hover:text-zinc-950 p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">URL <span className="text-red-500 normal-case">*</span></label>
            <div className="flex gap-2">
              <input
                data-testid="add-link-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.brownells.com/…"
                className="flex-1 text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm font-mono"
              />
              <button
                data-testid="add-link-fetch"
                type="button"
                onClick={fetchPreview}
                disabled={fetching || !url.trim()}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50 rounded-sm"
              >
                {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Fetch preview
              </button>
            </div>
          </div>

          {ogImage && (
            <div className="border border-zinc-200 rounded-sm overflow-hidden aspect-[16/6] relative bg-zinc-100">
              <img src={ogImage} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          )}

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Title</label>
            <input
              data-testid="add-link-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Vortex Diamondback — $50 Off"
              maxLength={200}
              className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm"
            />
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Description</label>
            <textarea
              data-testid="add-link-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short pitch — 1-2 sentences"
              rows={3}
              maxLength={500}
              className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm resize-y"
            />
            <div className="text-xs text-zinc-400 mt-1 font-mono">{description.length}/500</div>
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Image URL</label>
            <input
              data-testid="add-link-image"
              value={ogImage}
              onChange={(e) => setOgImage(e.target.value)}
              placeholder="https://…jpg"
              className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-2">Category</label>
              <select
                data-testid="add-link-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full text-sm bg-white border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm"
              >
                <option value="">Uncategorized</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Expires (optional)
              </label>
              <input
                data-testid="add-link-expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="w-full text-sm border border-zinc-200 focus:border-zinc-950 outline-none px-3 py-2 rounded-sm"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 pt-2 cursor-pointer">
            <input
              data-testid="add-link-publish-now"
              type="checkbox"
              checked={publishNow}
              onChange={(e) => setPublishNow(e.target.checked)}
              className="w-4 h-4 accent-zinc-950"
            />
            <span className="text-sm">Publish to public page immediately</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            data-testid="add-link-cancel"
            onClick={onClose}
            className="text-sm px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-sm"
          >
            Cancel
          </button>
          <button
            data-testid="add-link-save"
            onClick={save}
            disabled={saving || !url.trim()}
            className="text-sm px-5 py-2 bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50 rounded-sm inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : publishNow ? "Publish" : "Save draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
